import { useState } from "react";
import { useRoute, useLocation } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import ModuleSlideViewer from "@/components/ModuleSlideViewer";
import ModuleQuizFinal, { type QuizQuestion } from "@/components/ModuleQuizFinal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { trpc } from "@/lib/trpc";
import { Loader2, Trophy, CheckCircle2, XCircle, BookOpen, ChevronDown } from "lucide-react";
import { toast } from "sonner";

/**
 * Learning Path Page (New Version)
 * Display slides and final quiz for a specific learning module
 */
export default function LearningPathNew() {
  const [, params] = useRoute("/learn/:pathId");
  const [, setLocation] = useLocation();
  const pathId = params?.pathId ?? null;

  const [showQuiz, setShowQuiz] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [userAnswers, setUserAnswers] = useState<Record<string, string>>({});

  // Fetch module data with slides and questions
  const { data, isLoading } = trpc.learn.getSlidesByPath.useQuery(
    { pathId: pathId! },
    { enabled: !!pathId }
  );

  const handleSlidesComplete = () => {
    setShowQuiz(true);
  };

  const submitQuizMutation = trpc.learn.submitQuiz.useMutation();

  const handleQuizComplete = async (answers: Record<string, string>) => {
    setUserAnswers(answers);
    setQuizCompleted(true);

    // Calculate score
    const questions = data?.questions || [];
    let correctCount = 0;
    questions.forEach((q) => {
      if (answers[q.id] === q.correct_answer) {
        correctCount++;
      }
    });

    const percentage = (correctCount / questions.length) * 100;
    const passed = percentage >= 70;

    if (passed) {
      toast.success("🎉 Congratulations! You passed the quiz!");
    } else {
      toast.error("You didn't pass this time. Try again!");
    }

    // Save result to database
    try {
      await submitQuizMutation.mutateAsync({
        moduleId: pathId!,
        score: correctCount,
        maxScore: questions.length,
        passed,
        answers,
      });
    } catch (error) {
      console.error("Failed to save quiz result:", error);
      toast.error("Failed to save your result. Please try again.");
    }
  };

  const handleRetry = () => {
    setShowQuiz(false);
    setQuizCompleted(false);
    setShowReview(false);
    setUserAnswers({});
  };

  const handleBackToModules = () => {
    setLocation("/learn");
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!data || !data.module) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Module not found</p>
          <Button onClick={handleBackToModules} className="mt-4">
            Back to Learning Center
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const { module, slides, questions } = data;

  // Calculate score if quiz completed
  let score = 0;
  let percentage = 0;
  let passed = false;

  if (quizCompleted) {
    questions?.forEach((q) => {
      if (userAnswers[q.id] === q.correct_answer) {
        score++;
      }
    });
    percentage = (score / (questions?.length || 1)) * 100;
    passed = percentage >= 70;
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <BookOpen className="h-4 w-4" />
            <span>Learning Center</span>
            <span>/</span>
            <span>{module.title}</span>
          </div>

          <h1 className="text-3xl font-bold tracking-tight">{module.title}</h1>
          
          {module.description && (
            <p className="text-muted-foreground">{module.description}</p>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            {module.level && (
              <Badge variant="outline">{module.level}</Badge>
            )}
            {module.sector && (
              <Badge variant="secondary">{module.sector}</Badge>
            )}
            {module.region && (
              <Badge variant="secondary">{module.region}</Badge>
            )}
            {module.duration_minutes && (
              <Badge variant="outline">{module.duration_minutes} min</Badge>
            )}
          </div>
        </div>

        {/* Content */}
        {quizCompleted ? (
          <div className="space-y-6">
            {/* Score Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {passed ? (
                    <>
                      <Trophy className="h-6 w-6 text-yellow-600" />
                      Module Completed!
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-6 w-6 text-muted-foreground" />
                      Quiz Completed
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert variant={passed ? "default" : "destructive"}>
                  <AlertDescription className="text-lg">
                    {passed ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-6 w-6" />
                        <span>Congratulations! You passed with {percentage.toFixed(0)}%</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <XCircle className="h-6 w-6" />
                        <span>You scored {percentage.toFixed(0)}%. You need 70% to pass.</span>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>

                <div className="text-center">
                  <p className="text-2xl font-bold">
                    {score} / {questions?.length || 0}
                  </p>
                  <p className="text-muted-foreground">Correct answers</p>
                </div>

                {!showReview && (
                  <div className="text-center">
                    <Button 
                      onClick={() => setShowReview(true)}
                      variant="outline"
                      className="gap-2"
                    >
                      <ChevronDown className="h-4 w-4" />
                      Voulez-vous en savoir plus ?
                    </Button>
                  </div>
                )}

                <div className="flex gap-2 justify-center">
                  <Button variant="outline" onClick={handleRetry}>
                    Retry Module
                  </Button>
                  <Button onClick={handleBackToModules}>
                    Back to Learning Center
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Review Questions (only shown after clicking "En savoir plus") */}
            {showReview && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Review</h3>
                {questions?.map((question: QuizQuestion, idx: number) => {
                  const userAnswer = userAnswers[question.id];
                  const isCorrect = userAnswer === question.correct_answer;

                  return (
                    <Card key={question.id} className={`border-l-4 ${isCorrect ? 'border-l-green-500' : 'border-l-red-500'}`}>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          {isCorrect ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-600" />
                          )}
                          Question {idx + 1}: {question.question}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {/* All answers with highlighting */}
                        <div className="space-y-2">
                          {['A', 'B', 'C'].map((letter) => {
                            const answerKey = `answer_${letter.toLowerCase()}` as keyof QuizQuestion;
                            const answerText = question[answerKey] as string;
                            const isUserAnswer = userAnswer === letter;
                            const isCorrectAnswer = question.correct_answer === letter;

                            return (
                              <div 
                                key={letter}
                                className={`p-3 rounded-md border ${
                                  isCorrectAnswer 
                                    ? 'bg-green-50 border-green-500 dark:bg-green-950/20' 
                                    : isUserAnswer 
                                    ? 'bg-red-50 border-red-500 dark:bg-red-950/20' 
                                    : 'bg-muted border-border'
                                }`}
                              >
                                <div className="flex items-start gap-2">
                                  <span className="font-semibold">{letter}.</span>
                                  <span className="flex-1">{answerText}</span>
                                  {isCorrectAnswer && (
                                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                                  )}
                                  {isUserAnswer && !isCorrectAnswer && (
                                    <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Explanation */}
                        {question.explanation && (
                          <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-md border border-blue-200 dark:border-blue-800">
                            <p className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                              💡 Explication
                            </p>
                            <p className="text-blue-800 dark:text-blue-200">
                              {question.explanation}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        ) : showQuiz ? (
          <ModuleQuizFinal
            questions={questions || []}
            moduleTitle={module.title}
            onComplete={handleQuizComplete}
          />
        ) : (
          <ModuleSlideViewer
            slides={slides || []}
            onComplete={handleSlidesComplete}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

